import { useReactFlow } from "@xyflow/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { emitCanvasFocusNodes } from "../constants/canvasFocusNodes.constants";
import {
  parseCanvasNodeHash,
  parseCanvasViewportHash,
  stripCanvasViewportHash,
} from "./canvasViewportHash";
import { useCanvasViewportNavigator } from "./useCanvasViewportNavigator";

/** Linked nodes arrive and get measured after the page's own viewport: how
 *  long to wait between fit attempts, and how many per hash. */
const NODE_FIT_RETRY_MS = 500;
const NODE_FIT_RETRIES = 20;

/**
 * The URL adapter for canvas targets: honors `#x=&y=&z=&t=` on a canvas
 * route (see canvasViewportHash) by handing it to the navigator, and
 * `#node=` / `#nodes=` by fitting the viewport to those nodes. The move
 * waits for `ready` — the page's one-shot initial viewport (restore or fit)
 * has landed — and for the pan-zoom to exist, so nothing set up at load
 * overwrites it. The keys are then stripped with a replace navigation that
 * keeps the query string, the rest of the hash and the location state
 * (panes live there): a reload does not re-run the move and Back gains no
 * entry. A target that could not be applied keeps its hash, so a reload
 * tries again. Each new hash applies once, so editing the hash in the
 * address bar or following a link to another canvas moves the viewport on
 * arrival.
 */
export function useCanvasViewportDeepLink({ ready }: { ready: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigateViewport = useCanvasViewportNavigator();
  // The navigator declines until the pan-zoom exists; this is what re-runs
  // the effect when it does.
  const { viewportInitialized, getNodes } = useReactFlow();
  const target = useMemo(
    () => parseCanvasViewportHash(location.hash),
    [location.hash],
  );
  const nodeIds = useMemo(
    () => parseCanvasNodeHash(location.hash),
    [location.hash],
  );
  const appliedRef = useRef<string | null>(null);
  // Retries are per hash: a target that spends its budget must not starve
  // the next one.
  const triesRef = useRef({ key: "", n: 0 });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!target && !nodeIds) {
      appliedRef.current = null;
      return;
    }
    if (!ready || !viewportInitialized) return;
    const key = JSON.stringify({ target, nodeIds });
    if (appliedRef.current === key) return;
    if (triesRef.current.key !== key) triesRef.current = { key, n: 0 };
    if (nodeIds) {
      // Fit once no linked node is still being measured (a hidden node never
      // is, and one that is not on the canvas is simply skipped), and only
      // when something could be fitted at all.
      const nodes = getNodes();
      const settling = nodeIds.some((id) =>
        nodes.some(
          (n) =>
            n.id === id &&
            !n.hidden &&
            !(n.measured?.width && n.measured?.height),
        ),
      );
      if (settling || !emitCanvasFocusNodes(nodeIds)) {
        if (triesRef.current.n >= NODE_FIT_RETRIES) return;
        triesRef.current.n += 1;
        const timer = window.setTimeout(
          () => setTick((n) => n + 1),
          NODE_FIT_RETRY_MS,
        );
        return () => window.clearTimeout(timer);
      }
    } else if (target && !navigateViewport(target)) {
      return;
    }
    appliedRef.current = key;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: stripCanvasViewportHash(location.hash),
      },
      { replace: true, state: location.state },
    );
  }, [
    target,
    nodeIds,
    tick,
    ready,
    viewportInitialized,
    getNodes,
    navigateViewport,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    location.state,
  ]);
}
